import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestService } from './rest.service';
import { map } from 'rxjs/operators';

export interface TestSummary {
  totalTests: number;
  passed: number;
  failed: number;
  passRate: number;
  automationRate: number;
  totalExecutions: string;
  reportGenerated: number;
  avgTestDuration: string;
}

export interface TrendData {
  week: string;
  passed: number;
  failed: number;
  blocked: number;
}

export interface CategoryStats {
  category: string;
  total: number;
  passed: number;
  failed: number;
  blocked: number;
  passRate: number;
}

export interface TestExecution {
  id: string;
  testCaseId: string;
  description: string;
  category: string;
  priority: string;
  status: 'Passed' | 'Failed' | 'Skipped' | 'Blocked';
  executedBy: string;
  executedDate: Date;
  duration: number;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReportDashboardService {

  constructor(
    private restService: RestService
  ) { }

  getTotalTestCases(): Observable<number> {
    return this.restService.get('executiveDashboardSummary', null)
      .pipe(map((res: any) => res.data));
  }

  getSummary(accountId?: string, projectId?: string | null): Observable<TestSummary> {
    const payload: any = { accountId: accountId };
    if (projectId) {
      payload.projectId = projectId;
    }
    return this.restService.post('executiveDashboardSummary', payload, null)
      .pipe(map((res: any) => res.data));
  }

  getTopFailingTests(accountId?: string, projectId?: string | null): Observable<any[]> {
    const payload: any = { accountId: accountId };
    if (projectId) {
      payload.projectId = projectId;
    }
    return this.restService.post('topFailingTests', payload, null)
      .pipe(map((res: any) => res.data || []));
  }

  getUserResponseData(payload: any): Observable<any> {
    return this.restService.post('userResponseData', payload);
  }

  getPassRateByCategory(accountId?: string, projectId?: string | null): Observable<any[]> {
    const payload: any = { accountId: accountId };
    if (projectId) {
      payload.projectId = projectId;
    }
    return this.restService.post('passRateByCategory', payload, null)
      .pipe(map((res: any) => res.data || []));
  }

  getDefectSeverityReport(accountId?: string, projectId?: string | null): Observable<any> {
    const payload: any = { accountId: accountId };
    if (projectId) {
      payload.projectId = projectId;
    }
    return this.restService.post('severityPercentage', payload, null)
      .pipe(map((res: any) => res.data || []));
  }

  getDashboardCards(period: any, accountId?: string, projectId?: string | null) {
    const payload: any = { timeRange: period };
    if (accountId) {
      payload.accountId = accountId;
    }
    if (projectId) {
      payload.projectId = projectId;
    }
    return this.restService.post('QualityMatrixHead', payload, null);
  }

  getProjectDetails(payload: any) {
    return this.restService.post('getAdminDetails', payload, null)
  }

  getUserDetails(payload: any) {
    return this.restService.post('getUserDetails', payload, null)
  }

  getTestExecutionTrend(accountId?: string, projectId?: string | null) {
    const payload: any = { accountId: accountId };
    if (projectId) {
      payload.projectId = projectId;
    }
    return this.restService.post('trendExecution', payload, null)
      .pipe(map((res: any) => res.data || []));
  }

  getExecutionHours(timeRange: string, accountId?: string, projectId?: string | null) {
    const payload: any = { timeRange: timeRange };
    if (accountId) {
      payload.accountId = accountId;
    }
    if (projectId) {
      payload.projectId = projectId;
    }
    return this.restService.post('executionHours', payload, null)
      .pipe(map((res: any) => res.data || res));
  }

  getAdminDefaultAccount() {
    return this.restService.get('adminDefaultAccount', null);
  }

  getAccountProjects(accountId: string) {
    return this.restService.get('accountProjects', accountId, null);
  }
}
